import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Check, Star, AlertTriangle, Eye } from "lucide-react";

const STATUS_LABELS = {
  started: "Started",
  vendors_done: "Credits added",
  submitted: "Submitted",
  in_review: "In review",
  accepted: "Accepted",
  not_this_time: "Not this time",
  in_publishing: "In publishing",
  published: "Published",
};
const STATUS_FLOW = ["submitted", "in_review", "accepted", "not_this_time", "in_publishing", "published"];
const CONSENT_BOX_LABELS = {
  terms: "Submission Terms & Privacy Policy",
  lovers_agree: "Lovers agree to being published",
  photo_permission: "Photographer & photo permission",
  pro_promises: "Pro: lovers agree + photo permission",
  open_to_either: "Open to either publication",
  newsletter: "Newsletter opt-in",
  coeditor_lovers_agree: "Invited lover: agrees to publication",
};

const PHOTO_PERMISSIONS = [
  { v: "granted", l: "Granted" },
  { v: "needed", l: "Needed" },
  { v: "not_needed", l: "Not needed" },
  { v: "requested", l: "Requested" },
];

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [credits, setCredits] = useState({ website_html: "", social_text: "" });
  const [copied, setCopied] = useState(null);
  const [featureUrl, setFeatureUrl] = useState("");
  const [galleryLink, setGalleryLink] = useState("");
  const [galleryPasswordInput, setGalleryPasswordInput] = useState("");
  const [staffNotes, setStaffNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [gallerySaving, setGallerySaving] = useState(false);
  const [galleryPw, setGalleryPw] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishFromWordPress, setPublishFromWordPress] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [publishUrl, setPublishUrl] = useState("");
  const [scheduledPublishDate, setScheduledPublishDate] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [prodRows, setProdRows] = useState([]);
  const [savingProduction, setSavingProduction] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [loversEmail, setLoversEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const resendLoversInvite = async () => {
    setInviteBusy(true);
    try {
      await api.post(`/admin/events/${id}/lovers-invite`,
                     { email: loversEmail.trim() || null });
      toast.success("Invite resent — a fresh 30-day link went out and the day-20/30 reminders restarted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Resend failed");
    } finally {
      setInviteBusy(false);
    }
  };

  const hardDelete = async () => {
    setDeleting(true);
    try {
      await api.post(`/admin/events/${id}/delete-including-files`,
                     { confirm_name: deleteName });
      toast.success("Submission and its files deleted");
      navigate("/admin/events");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    const e = await api.get(`/admin/events/${id}`);
    setEvent(e.data);
    setFeatureUrl(e.data.feature_url || "");
    setGalleryLink(e.data.gallery_link || "");
    setStaffNotes(e.data.staff_notes || "");
    setLoversEmail(e.data.client_email || "");
    const c = await api.get(`/admin/events/${id}/credits`);
    setCredits(c.data);
    try {
      const p = await api.get(`/admin/events/${id}/production`);
      setProdRows(p.data.rows || []);
    } catch { /* older events may not have production yet */ }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const copy = async (kind) => {
    const text = kind === "website" ? credits.website_html : credits.social_text;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(kind);
    toast.success("Copied!");
    setTimeout(() => setCopied(null), 1500);
  };

  const patch = async (body, msg) => {
    try {
      await api.patch(`/admin/events/${id}`, body);
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const setStatus = async (status) => {
    try {
      const { data } = await api.post(`/admin/events/${id}/status`, { status });
      if (data?.status !== status) {
        await load();
        throw new Error(`The server did not confirm the status change. Current status: ${data?.status || "unknown"}.`);
      }
      setEvent(data);
      try {
        const { data: production } = await api.get(`/admin/events/${id}/production`);
        setProdRows(production.rows || []);
      } catch {
        // Checklist refresh is separate from the saved status; don't label a
        // successful status change as failed because this panel could not load.
      }
      toast.success(`Status saved → ${STATUS_LABELS[status] || status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Status change failed");
    }
  };

  const saveGalleryDetails = async () => {
    setGallerySaving(true);
    try {
      const payload = { gallery_link: galleryLink.trim() };
      if (galleryPasswordInput.trim()) payload.gallery_password = galleryPasswordInput.trim();
      const { data } = await api.patch(`/admin/events/${id}`, payload);
      setEvent(data);
      setGalleryPasswordInput("");
      setGalleryPw(null);
      toast.success("Gallery details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gallery details could not be saved");
    } finally {
      setGallerySaving(false);
    }
  };

  const saveStaffNotes = async () => {
    setNotesSaving(true);
    try {
      const { data } = await api.patch(`/admin/events/${id}`, { staff_notes: staffNotes });
      setEvent(data);
      toast.success("Staff notes saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Staff notes could not be saved");
    } finally {
      setNotesSaving(false);
    }
  };

  const revealGalleryPw = async () => {
    try {
      const { data } = await api.get(`/admin/events/${id}/gallery-password`);
      setGalleryPw(data.gallery_password || "(none set)");
    } catch {
      toast.error("Could not load gallery password");
    }
  };

  const openPublish = async (fromWordPress = false) => {
    setPublishFromWordPress(fromWordPress);
    setPublishOpen(true);
    setPublishUrl(featureUrl || "");
    setScheduledPublishDate(event?.scheduled_publish_date || "");
    try {
      const { data } = await api.get(`/admin/events/${id}/publish-checklist`);
      setChecklist(data);
    } catch {
      setChecklist({ can_publish: false, missing: ["Could not load checklist"] });
    }
  };

  const doPublish = async () => {
    if (!publishUrl.trim()) return toast.error("Paste the live feature URL first.");
    if (!scheduledPublishDate) return toast.error("Choose a scheduled publish date.");
    setPublishing(true);
    try {
      await api.post(`/admin/events/${id}/publish`, {
        feature_url: publishUrl.trim(),
        ...(scheduledPublishDate ? { scheduled_publish_date: scheduledPublishDate } : {}),
      });
      toast.success("Published!");
      setPublishOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const retryGhl = async () => {
    try {
      const { data } = await api.post(`/admin/events/${id}/ghl-retry`);
      const st = data.ghl?.sync_status;
      toast.success(st === "ok" ? "GHL sync OK" : `GHL sync: ${st || "retried"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Retry failed");
    }
  };

  const tickProduction = async (item, done) => {
    if (item === "added_to_wordpress" && done) {
      openPublish(true);
      return;
    }
    setSavingProduction((current) => ({ ...current, [item]: true }));
    try {
      const { data } = await api.post(`/admin/events/${id}/production/${item}`, { done });
      setEvent(data);
      const { data: production } = await api.get(`/admin/events/${id}/production`);
      setProdRows(production.rows || []);
      toast.success(done ? "Checklist item marked done" : "Checklist item reopened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checklist update failed");
    } finally {
      setSavingProduction((current) => ({ ...current, [item]: false }));
    }
  };

  const regenerateLists = async () => {
    try {
      const { data } = await api.post(`/admin/events/${id}/regenerate-lists`);
      setCredits({ website_html: data.website_html, social_text: data.social_text });
      toast.success("Vendor lists regenerated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Regenerate failed");
    }
  };

  const regenerateDraft = async () => {
    try {
      const { data } = await api.post(`/admin/events/${id}/regenerate-draft`);
      if (data?.draft_in_progress) {
        toast.info("Draft in progress");
        setTimeout(load, 15000);
      } else {
        toast.success("Draft generating — refresh in a minute");
        setTimeout(load, 45000);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Draft failed to start");
    }
  };

  if (!event) return <div className="p-8 text-sm text-[#5c5852]">Loading…</div>;

  const consent = event.consent || {};
  const pro = event.pro || {};

  return (
    <div className="p-6 lg:p-10" data-testid="event-detail-page">
      <Link to="/admin/events" className="btw-overline hover:underline">← All events</Link>
      <div className="mt-3 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="btw-overline mb-1">
            {event.publication.toUpperCase()} · {STATUS_LABELS[event.status] || event.status}
            {event.submitter_type === "pro" ? " · via pro" : ""}
          </p>
          <h1 className="font-serif text-4xl inline-flex items-center gap-3">
            {event.event_name}
            {event.priority && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-[#c19872]/20 text-[#876a4f] rounded-full" data-testid="priority-badge">
                <Star size={12} className="fill-[#c19872]" /> PRIORITY
              </span>
            )}
            {event.open_to_either && (
              <span className="text-xs px-3 py-1 border border-[#c19872] text-[#876a4f] rounded-full" data-testid="open-to-either-badge">
                Open to either
              </span>
            )}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {event.is_live && (
            <span className="text-xs px-3 py-2 bg-[#768c6a]/20 text-[#5c5852]" data-testid="live-pill">● Live</span>
          )}
        </div>
      </div>

      {event.partner_flag && (
        <div className="mb-6 bg-white border border-[#c19872] p-4 text-sm flex items-center gap-3" data-testid="partner-flag-banner">
          <AlertTriangle size={16} className="text-[#876a4f]" />
          <span>The submitting pro says they're a paid partner, but their business isn't starred in the vendor directory. Worth a check.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Status control + history */}
        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="status-section">
          <h2 className="btw-overline mb-4">Status</h2>
          <div className="flex flex-wrap gap-2 mb-5">
            {STATUS_FLOW.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => (s === "published" && event.status !== "published" ? openPublish() : setStatus(s))}
                disabled={event.status === s}
                data-testid={`set-status-${s}`}
                className={`text-xs px-3 py-2 border transition-colors ${
                  event.status === s
                    ? "border-[#c19872] bg-[#c19872]/20 text-[#876a4f]"
                    : "border-[#dcd3c6] text-[#5c5852] hover:border-[#c19872]"
                }`}
              >
                {s === "published" ? "Publish…" : STATUS_LABELS[s]}
              </button>
            ))}
          </div>

          {publishOpen && event.status !== "published" && (
            <div className="mb-5 p-4 border border-[#c19872] bg-[#f5f3ed]" data-testid="publish-panel">
              <p className="btw-overline mb-3">{publishFromWordPress ? "Add to WordPress & schedule GHL email" : "Publish checklist"}</p>
              {checklist === null ? (
                <p className="text-xs text-[#5c5852]">Checking…</p>
              ) : (checklist.can_publish || (publishFromWordPress && (checklist.missing || []).every((m) => m === "Production: Added to WordPress"))) ? (
                <p className="text-xs text-[#768c6a] mb-3" data-testid="checklist-ok">All good — photographer permission sorted, every credited vendor verified.</p>
              ) : (
                <ul className="text-xs text-[#b35c5c] space-y-1 mb-3" data-testid="checklist-missing">
                  {(checklist.missing || []).filter((m) => !(publishFromWordPress && m === "Production: Added to WordPress")).map((m, i) => <li key={i}>• {m}</li>)}
                </ul>
              )}
              <label className="btw-overline block mb-2">Live feature URL</label>
              <input
                data-testid="publish-url-input"
                type="url"
                value={publishUrl}
                onChange={(e) => setPublishUrl(e.target.value)}
                placeholder="https://dancingwithher.com/…"
                className="w-full bg-white border border-[#dcd3c6] px-3 py-2 text-sm mb-3"
              />
              <label className="btw-overline block mb-2">Scheduled Publish Date</label>
              <input
                data-testid="scheduled-publish-date-input"
                type="date"
                value={scheduledPublishDate}
                onChange={(e) => setScheduledPublishDate(e.target.value)}
                className="w-full bg-white border border-[#dcd3c6] px-3 py-2 text-sm mb-3"
              />
              <p className="text-xs text-[#5c5852] mb-3">GHL will send the "story is live" email at 10:00 AM on this date. Both the live URL and date are required.</p>
              <div className="flex gap-2">
                <button
                  onClick={doPublish}
                  disabled={publishing || !(checklist?.can_publish || (publishFromWordPress && (checklist?.missing || []).every((m) => m === "Production: Added to WordPress"))) || !publishUrl.trim() || !scheduledPublishDate}
                  data-testid="publish-button"
                  className="text-xs px-4 py-2 bg-[#c19872] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {publishing ? "Saving…" : publishFromWordPress ? "Save URL, date & sync GHL" : "Publish & sync GHL"}
                </button>
                <button onClick={() => { setPublishOpen(false); setPublishFromWordPress(false); }} data-testid="publish-cancel" className="text-xs px-4 py-2 border border-[#dcd3c6] text-[#5c5852]">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {event.publication === "either" && (
            <div className="mb-5 p-3 border border-[#c19872] bg-[#e2d3c0]/20" data-testid="assign-publication">
              <label className="btw-overline block mb-2">Assign a publication (pro said "consider for either")</label>
              <div className="flex gap-2">
                <button onClick={() => patch({ publication: "dwh" }, "Assigned to DWH")} data-testid="assign-dwh" className="text-xs px-3 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">Dancing With Her</button>
                <button onClick={() => patch({ publication: "dwt" }, "Assigned to DWT")} data-testid="assign-dwt" className="text-xs px-3 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">Dancing With Them</button>
              </div>
            </div>
          )}
          {["dwh", "dwt"].includes(event.publication) &&
            ["submitted", "in_review"].includes(event.status) && (
            <div className="mb-5 p-3 border border-[#c19872] bg-[#e2d3c0]/20" data-testid="switch-publication">
              <label className="btw-overline block mb-2">
                Switch publication{event.open_to_either ? " — submitter is open to either" : ""}
              </label>
              <p className="text-xs text-[#5c5852] mb-2">
                Moves the GHL tags to the other sub-account.
              </p>
              <button
                onClick={() => patch(
                  { publication: event.publication === "dwh" ? "dwt" : "dwh" },
                  `Switched to ${event.publication === "dwh" ? "DWT" : "DWH"}`)}
                data-testid="switch-publication-btn"
                className="text-xs px-3 py-2 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40"
              >
                Switch to {event.publication === "dwh" ? "Dancing With Them" : "Dancing With Her"}
              </button>
            </div>
          )}
          <div className="mt-6 pt-5 border-t border-[#dcd3c6]" data-testid="staff-notes-section">
            <label htmlFor="staff-notes" className="btw-overline block mb-2">Staff notes · private</label>
            <p className="text-xs text-[#5c5852] mb-2">Only visible to the editorial team.</p>
            <textarea id="staff-notes" data-testid="staff-notes-input" value={staffNotes}
              onChange={(e) => setStaffNotes(e.target.value)} rows={5}
              className="w-full bg-white border border-[#dcd3c6] px-3 py-2 text-sm"
              placeholder="Add internal notes for the team…" />
            <button type="button" onClick={saveStaffNotes} disabled={notesSaving}
              data-testid="save-staff-notes"
              className="mt-2 text-xs px-4 py-2 border border-[#c19872] text-[#876a4f] disabled:opacity-50">
              {notesSaving ? "Saving…" : "Save notes"}
            </button>
          </div>
        </section>

        {/* Meta */}
        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="meta-section">
          <h2 className="btw-overline mb-4">Event details</h2>
          <dl className="text-sm space-y-2 mb-4">
            <Meta k="Publication" v={event.publication.toUpperCase()} />
            <Meta k="Status" v={STATUS_LABELS[event.status] || event.status} />
            <Meta k="Story type" v={event.story_label || event.story_category || "—"} />
            <Meta k="Contact email" v={event.client_email || "—"} />
            {event.event_city || event.event_country ? (
              <Meta k="Event location" v={[event.event_city, event.event_country].filter(Boolean).join(", ")} />
            ) : null}
            <Meta k="Vendors" v={event.vendors.length} />
            <Meta k="Live" v={event.is_live ? "Yes" : "No"} />
          </dl>

          <div className="pt-4 border-t border-[#dcd3c6] space-y-4">
            <div>
              <label className="btw-overline block mb-2">Feature URL (published link)</label>
              <input
                data-testid="feature-url-input"
                type="url"
                value={featureUrl}
                onChange={(e) => setFeatureUrl(e.target.value)}
                onBlur={() => featureUrl !== (event.feature_url || "") && patch({ feature_url: featureUrl }, "Feature URL saved")}
                placeholder="https://dancingwithher.com/casey-and-riley"
                className="w-full bg-transparent border-b border-[#dcd3c6] focus:border-[#c19872] outline-none py-2 text-sm"
              />
            </div>
            <div>
              <label className="btw-overline block mb-2">Photographer permission</label>
              <select
                data-testid="photographer-permission"
                value={event.photographer_permission || "not_needed"}
                onChange={(e) => patch({ photographer_permission: e.target.value }, "Photographer permission updated")}
                className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white text-sm"
              >
                {PHOTO_PERMISSIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              {event.photographer_info && event.photographer_info.email && (
                <p className="text-xs text-[#5c5852] mt-2" data-testid="photographer-info">
                  Photographer: {event.photographer_info.name}
                  {event.photographer_info.business_name ? ` (${event.photographer_info.business_name})` : ""} — {event.photographer_info.email}
                </p>
              )}
            </div>
            <div>
              <label className="btw-overline block mb-2">Gallery</label>
              <div className="text-sm space-y-2">
                <div data-testid="gallery-link">
                  {event.gallery_link ? (
                    <a href={event.gallery_link} target="_blank" rel="noreferrer" className="underline break-all">{event.gallery_link}</a>
                  ) : <span className="text-[#5c5852]">No gallery link</span>}
                </div>
                <label htmlFor="gallery-link-input" className="block text-xs text-[#5c5852]">Gallery URL</label>
                <input id="gallery-link-input" data-testid="gallery-link-input" type="url" value={galleryLink}
                  onChange={(e) => setGalleryLink(e.target.value)} placeholder="https://…"
                  className="w-full bg-white border border-[#dcd3c6] px-3 py-2 text-sm" />
                <label htmlFor="gallery-password-input" className="block text-xs text-[#5c5852]">Gallery password</label>
                <input id="gallery-password-input" data-testid="gallery-password-input" type="password"
                  value={galleryPasswordInput} onChange={(e) => setGalleryPasswordInput(e.target.value)}
                  placeholder={event.has_gallery_password ? "Enter to replace saved password" : "Enter gallery password"}
                  className="w-full bg-white border border-[#dcd3c6] px-3 py-2 text-sm" />
                <button type="button" onClick={saveGalleryDetails} disabled={gallerySaving}
                  data-testid="save-gallery-details"
                  className="text-xs px-3 py-2 border border-[#c19872] text-[#876a4f] disabled:opacity-50">
                  {gallerySaving ? "Saving…" : "Save gallery details"}
                </button>
                {event.has_gallery_password && (
                  <div className="flex items-center gap-3">
                    {galleryPw == null ? (
                      <button onClick={revealGalleryPw} data-testid="show-gallery-password" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
                        <Eye size={12} /> Show gallery password
                      </button>
                    ) : (
                      <code className="text-xs bg-[#f5f3ed] border border-[#dcd3c6] px-3 py-1.5" data-testid="gallery-password">{galleryPw}</code>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Pro details */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {event.submitter_type === "pro" && (
          <section className="bg-white border border-[#dcd3c6] p-5" data-testid="pro-section">
            <h2 className="btw-overline mb-4">Submitted by a pro</h2>
            <dl className="text-sm space-y-2">
              <Meta k="Name" v={pro.name || "—"} />
              <Meta k="Business" v={pro.business_name || "—"} />
              <Meta k="Category" v={pro.vendor_category || "—"} />
              <Meta k="Email" v={pro.email || "—"} />
              {pro.instagram && <Meta k="Instagram" v={`@${pro.instagram}`} />}
              {pro.website && <Meta k="Website" v={pro.website} />}
              <Meta k="Paid partner?" v={({ dwh: "DWH", dwt: "DWT", both: "Both", neither: "Neither" })[pro.partner_status] || "Neither"} />
              <Meta k="Mailing list" v={event.mailing_opt_in ? "Yes" : "No"} />
              <Meta k="Partnership interest" v={event.partnership_opt_in ? "Yes" : "No"} />
            </dl>
            {event.story_description && (
              <div className="mt-4 pt-4 border-t border-[#dcd3c6]">
                <p className="btw-overline mb-2">Why they're sharing it</p>
                <p className="text-sm text-[#5c5852] whitespace-pre-wrap" data-testid="story-description">{event.story_description}</p>
              </div>
            )}
            {event.referral && (
              <div className="mt-4 pt-4 border-t border-[#dcd3c6]" data-testid="referral-panel">
                <p className="btw-overline mb-2">Lovers' invite (pro referral)</p>
                <p className="text-xs text-[#5c5852] mb-3" data-testid="referral-status">
                  {event.referral.accepted_at
                    ? `Accepted ${new Date(event.referral.accepted_at).toLocaleDateString()} — the invite link is closed`
                    : `Invited ${event.referral.invited_at ? new Date(event.referral.invited_at).toLocaleDateString() : "—"} · not accepted yet`
                      + (event.referral.day20_sent_at ? " · day-20 notice sent" : "")
                      + (event.referral.day30_sent_at ? " · day-30 notice sent" : "")}
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  <input
                    type="email"
                    data-testid="lovers-email-input"
                    aria-label="Lovers' email"
                    className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white text-sm flex-1 min-w-[220px]"
                    value={loversEmail}
                    onChange={(e) => setLoversEmail(e.target.value)}
                  />
                  <button
                    type="button"
                    data-testid="resend-lovers-invite"
                    onClick={resendLoversInvite}
                    disabled={inviteBusy}
                    className="btw-btn-outline text-xs px-3 py-2"
                  >
                    {inviteBusy ? "Sending…"
                      : (loversEmail.trim().toLowerCase() !== (event.client_email || "").toLowerCase() && loversEmail.trim()
                        ? "Change email & resend invite" : "Resend invite")}
                  </button>
                </div>
                <p className="text-xs text-[#5c5852] mt-2">
                  Resending starts a fresh 30-day link, stops every old link, and restarts the day-20 / day-30 pro reminders.
                </p>
                {(event.referral.log || []).length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="referral-log">
                    {event.referral.log.slice().reverse().slice(0, 5).map((l, i) => (
                      <li key={i} className="text-xs text-[#5c5852]">
                        {new Date(l.at).toLocaleString()} — {String(l.action).replaceAll("_", " ")}
                        {l.detail ? ` (${l.detail})` : ""} · {l.by}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Production checklist */}
      {prodRows.length > 0 && (
        <section className="bg-white border border-[#dcd3c6] p-5 mb-8" data-testid="production-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="btw-overline">Production checklist</h2>
            <div className="flex gap-2">
              <button onClick={regenerateDraft} data-testid="regenerate-draft" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
                Regenerate draft
              </button>
              <button onClick={regenerateLists} data-testid="regenerate-lists" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
                Regenerate vendor lists
              </button>
            </div>
          </div>
          <div className="divide-y divide-[#dcd3c6]/60">
            {prodRows.map((r) => (
              <div key={r.key} className="py-3 flex flex-wrap items-center gap-3" data-testid={`prod-${r.key}`}>
                <span className={`w-24 shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 text-center ${
                  r.done ? "bg-[#768c6a]/20 text-[#5c6e53]"
                    : r.status === "not_configured" ? "bg-[#e2d3c0] text-[#5c5852]"
                    : r.status === "coming_soon" ? "bg-[#e2d3c0]/50 text-[#5c5852] italic"
                    : r.status === "running" ? "bg-[#c19872]/20 text-[#876a4f]"
                    : r.status === "error" ? "bg-[#b35c5c]/20 text-[#b35c5c]"
                    : "bg-[#b35c5c]/10 text-[#b35c5c]"
                }`} data-testid={`prod-status-${r.key}`}>
                  {r.done ? "Done" : r.status === "coming_soon" ? "Coming soon" : r.status === "not_configured" ? "Not configured" : r.status === "running" ? "Running" : r.status === "error" ? "Error" : "To do"}
                </span>
                <span className="text-sm font-medium flex-1 min-w-[160px]">{r.label}</span>
                {r.key === "drive_folder" && r.name && (
                  <span className="text-xs text-[#5c5852]" data-testid="drive-folder-name">
                    {r.link ? <a href={r.link} target="_blank" rel="noreferrer" className="underline">{r.name}</a> : r.name}
                    {r.duplicate && <span className="text-[#b35c5c] ml-1">(name already existed)</span>}
                  </span>
                )}
                {r.key === "gallery_created" && (
                  <span className="text-xs text-[#5c5852]" data-testid="gallery-row-info">
                    {event.gallery_link
                      ? <a href={event.gallery_link} target="_blank" rel="noreferrer" className="underline">Gallery link</a>
                      : "No gallery link"}
                    {event.has_gallery_password && (
                      galleryPw == null ? (
                        <button onClick={revealGalleryPw} className="ml-2 underline" data-testid="gallery-pw-inline">show password</button>
                      ) : (
                        <code className="ml-2 bg-[#f5f3ed] border border-[#dcd3c6] px-2 py-0.5" data-testid="gallery-pw-inline">{galleryPw}</code>
                      )
                    )}
                  </span>
                )}
                {r.note && <span className="text-xs text-[#5c5852]">{r.note}</span>}
                <span className="text-xs text-[#5c5852] ml-auto">
                  {r.done && r.at ? `${new Date(r.at).toLocaleString()}${r.by ? ` · ${r.by}` : ""}` : ""}
                </span>
                {r.mode === "manual" && !r.derived && (
                  <button
                    type="button"
                    onClick={() => tickProduction(r.key, !r.done)}
                    disabled={Boolean(savingProduction[r.key])}
                    data-testid={`prod-tick-${r.key}`}
                    className="text-xs px-3 py-1 border border-[#dcd3c6] text-[#5c5852] hover:border-[#c19872] hover:text-[#876a4f] disabled:opacity-50"
                  >
                    {savingProduction[r.key] ? "Saving…" : r.done ? "Undo" : "Mark done"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Blog draft */}
      {event.draft && (
        <section className="bg-white border border-[#dcd3c6] p-5 mb-8" data-testid="draft-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="btw-overline">Blog draft</h2>
            <div className="flex items-center gap-3 text-xs text-[#5c5852]">
              <span>{event.draft.model} · {event.draft.generated_at ? new Date(event.draft.generated_at).toLocaleString() : ""}</span>
              {event.draft.doc_link ? (
                <a href={event.draft.doc_link} target="_blank" rel="noreferrer" className="underline text-[#876a4f]" data-testid="draft-doc-link">Open Google Doc</a>
              ) : (
                <span className="italic" data-testid="draft-no-doc">{event.draft.doc_note || "Doc not written"}</span>
              )}
            </div>
          </div>

          <dl className="text-sm space-y-2 mb-5" data-testid="draft-seo">
            <Meta k="Blog title" v={event.draft.blog_title} />
            <Meta k="Focus keyphrase" v={event.draft.focus_keyphrase} />
            <Meta k={`SEO title (${(event.draft.seo_title || "").length})`} v={event.draft.seo_title} />
            <Meta k="Slug" v={event.draft.slug} />
            <Meta k={`Meta description (${(event.draft.meta_description || "").length})`} v={event.draft.meta_description} />
            <Meta k={`Excerpt (${(event.draft.excerpt || "").length})`} v={event.draft.excerpt} />
            <Meta k="Categories" v={(event.draft.categories || []).join(", ")} />
            <Meta k="Primary category" v={event.draft.primary_category} />
            <Meta k="Tags" v={(event.draft.tags || []).join(", ")} />
            <Meta k="Suggested new tags" v={(event.draft.suggested_new_tags || []).join(", ")} />
          </dl>

          <h3 className="btw-overline mb-2">Automatic checks</h3>
          <div className="space-y-1 mb-5" data-testid="draft-checks">
            {(event.draft.checks || []).map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-xs" data-testid={`check-${c.key}`}>
                <span className={`w-12 shrink-0 text-center px-1.5 py-0.5 uppercase tracking-wider ${c.ok ? "bg-[#768c6a]/20 text-[#5c6e53]" : "bg-[#b35c5c]/15 text-[#b35c5c]"}`}>
                  {c.ok ? "Pass" : "Check"}
                </span>
                <span>{c.label}</span>
                {c.detail && <span className="text-[#5c5852]">— {c.detail}</span>}
              </div>
            ))}
          </div>

          {(event.draft.flags || []).length > 0 && (
            <div className="mb-5" data-testid="draft-flags">
              <h3 className="btw-overline mb-2">Bracketed flags</h3>
              <ul className="text-xs text-[#b35c5c] space-y-1">
                {event.draft.flags.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
          )}

          <div className="grid sm:grid-cols-3 gap-4 text-xs text-[#5c5852]">
            <div data-testid="draft-image-placements">
              <h3 className="btw-overline mb-1">Image placements</h3>
              {(event.draft.image_placements || []).map((x, i) => <p key={i}>• {x}</p>)}
            </div>
            <div data-testid="draft-alt-texts">
              <h3 className="btw-overline mb-1">Alt text</h3>
              {(event.draft.alt_texts || []).map((x, i) => <p key={i}>• {x}</p>)}
            </div>
            <div data-testid="draft-banner-notes">
              <h3 className="btw-overline mb-1">Banner notes</h3>
              {(event.draft.banner_notes || []).map((x, i) => <p key={i}>• {x}</p>)}
            </div>
          </div>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="partners-section">
          <h2 className="btw-overline mb-4">The lovers</h2>
          <div className="space-y-3">
            {event.partners.length === 0 && (
              <p className="text-sm text-[#5c5852] italic">None listed (styled shoot).</p>
            )}
            {event.partners.map((p) => (
              <div key={p.id} className="border-b border-[#dcd3c6]/60 pb-3 last:border-0">
                <div className="font-serif italic text-xl">
                  {p.first_name} {p.last_name}
                </div>
                <div className="text-xs text-[#5c5852] mt-1 flex gap-3 flex-wrap">
                  {p.preferred_term && <span>Term: <strong>{p.preferred_term}</strong></span>}
                  {p.pronouns && <span>Pronouns: <strong>{p.pronouns}</strong></span>}
                  {p.pronoun_visibility && (
                    <span title="Where pronouns can be shown publicly">Show: <strong>{p.pronoun_visibility.replace(/_/g, " ")}</strong></span>
                  )}
                  {p.instagram && <span>@{p.instagram}{p.ok_to_tag ? " · OK to tag" : " · don't tag"}</span>}
                </div>
                {p.identity && (
                  <div className="text-xs text-[#5c5852] mt-1 italic">Identity: {p.identity}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-[#dcd3c6] p-5" data-testid="coeditors-section">
          <h2 className="btw-overline mb-4">People on this submission</h2>
          <dl className="text-sm space-y-2">
            <Meta k="Primary email" v={event.client_email || "—"} />
            {(event.co_editors || []).map((em) => (
              <Meta key={em} k="Co-editor" v={em} />
            ))}
          </dl>
        </section>
      </div>

      <section className="bg-white border border-[#dcd3c6] p-5 mb-8" data-testid="vendors-section">
        <h2 className="btw-overline mb-4">Vendors ({event.vendors.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-[#5c5852] border-b border-[#dcd3c6]">
            <tr>
              <th className="text-left py-2 w-6"></th>
              <th className="text-left py-2">Category</th>
              <th className="text-left py-2">Business</th>
              <th className="text-left py-2">Website</th>
              <th className="text-left py-2">Instagram</th>
              <th className="text-left py-2">Email</th>
            </tr>
          </thead>
          <tbody>
            {event.vendors.length === 0 && (
              <tr><td colSpan="6" className="py-6 text-center italic text-[#5c5852]">No vendors submitted.</td></tr>
            )}
            {event.vendors.map((v) => (
              <tr key={v.id} className="border-b border-[#dcd3c6]/40">
                <td className="py-2">
                  {v.is_partner && (
                    <Star size={14} className="text-[#876a4f] fill-[#c19872]" data-testid={`partner-star-${v.id}`} aria-label="Active partner" />
                  )}
                </td>
                <td className="py-2 font-medium">{v.vendor_type}</td>
                <td className="py-2">
                  {v.vendor_id ? (
                    <Link to={`/admin/vendors/${v.vendor_id}`} className="hover:text-[#876a4f]">{v.business_name}</Link>
                  ) : v.business_name}
                </td>
                <td className="py-2 text-[#5c5852] truncate max-w-[160px]">{v.website || "—"}</td>
                <td className="py-2 text-[#5c5852]">{v.instagram ? `@${v.instagram}` : "—"}</td>
                <td className="py-2 text-[#5c5852]">{v.email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {event.story_responses && event.story_responses.length > 0 && (
        <section className="bg-white border border-[#dcd3c6] p-5 mb-8" data-testid="story-section">
          <h2 className="btw-overline mb-1">Story responses</h2>
          <p className="text-xs text-[#5c5852] mb-4 italic">{event.story_label || event.story_category}</p>
          <div className="space-y-5">
            {event.story_responses.map((r, i) => (
              <div key={i} className="border-l border-[#dcd3c6] pl-4">
                <p className="font-serif text-base mb-1">{r.question}</p>
                <p className="text-sm text-[#5c5852] whitespace-pre-wrap">{r.answer || <em>— no answer —</em>}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#dcd3c6] p-5" data-testid="website-credits">
          <div className="flex items-center justify-between mb-4">
            <h2 className="btw-overline">Copy details for website</h2>
            <button onClick={() => copy("website")} data-testid="copy-website" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
              {copied === "website" ? <Check size={12} /> : <Copy size={12} />}
              {copied === "website" ? "Copied!" : "Copy HTML"}
            </button>
          </div>
          <pre className="text-xs whitespace-pre-wrap bg-[#f5f3ed] p-3 border border-[#dcd3c6] overflow-auto max-h-72">
{credits.website_html || "—"}
          </pre>
        </div>
        <div className="bg-white border border-[#dcd3c6] p-5" data-testid="social-credits">
          <div className="flex items-center justify-between mb-4">
            <h2 className="btw-overline">Copy details for socials</h2>
            <button onClick={() => copy("social")} data-testid="copy-social" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">
              {copied === "social" ? <Check size={12} /> : <Copy size={12} />}
              {copied === "social" ? "Copied!" : "Copy text"}
            </button>
          </div>
          <pre className="text-xs whitespace-pre-wrap bg-[#f5f3ed] p-3 border border-[#dcd3c6] overflow-auto max-h-72">
{credits.social_text || "—"}
          </pre>
        </div>
      </section>

      <details className="bg-white border border-[#dcd3c6] p-5 mb-4" data-testid="consent-section">
        <summary className="btw-overline cursor-pointer">Consent record</summary>
        <div className="pt-4">
          {consent.at || consent.boxes ? (
            <dl className="text-sm space-y-2">
              {(consent.boxes ? Object.entries(consent.boxes) : []).map(([key, b]) => (
                <div key={key} className="flex justify-between gap-4" data-testid={`consent-box-${key}`}>
                  <dt>{CONSENT_BOX_LABELS[key] || key}</dt>
                  <dd className="text-right text-[#2a2825]">{b.ticked ? "Yes" : "No"} · {b.by || "—"} · {b.at ? new Date(b.at).toLocaleString() : "—"} · v{b.version || "?"}</dd>
                </div>
              ))}
              {!consent.boxes && <>
                <Meta k="Permission to publish" v={consent.publish_permission ? "Yes" : "No"} />
                <Meta k="Terms & privacy accepted" v={consent.terms_accepted ? "Yes" : "No"} />
                <Meta k="Marketing opt-in" v={consent.marketing_opt_in ? "Yes" : "No"} />
              </>}
              {consent.at && <Meta k="Submitted" v={`${new Date(consent.at).toLocaleString()} · ${consent.by || ""}`} />}
            </dl>
          ) : <p className="text-sm text-[#5c5852] italic" data-testid="no-consent">Not submitted yet — consent is recorded on final submit.</p>}
        </div>
      </details>

      <details className="bg-white border border-[#dcd3c6] p-5 mb-4" data-testid="ghl-section">
        <summary className="btw-overline cursor-pointer">GoHighLevel sync</summary>
        <div className="pt-4">
          <div className="flex items-center justify-end mb-4">
            <button type="button" onClick={retryGhl} data-testid="ghl-retry" className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-[#c19872] text-[#876a4f] hover:bg-[#e2d3c0]/40">Retry GHL sync</button>
          </div>
          {event.publication === "either" ? (
            <p className="text-sm text-[#5c5852] italic" data-testid="ghl-either">No GHL sync yet — assign a publication first.</p>
          ) : (() => {
            const b = (event.ghl || {})[event.publication] || {};
            return <dl className="text-sm space-y-2">
              <Meta k="Sub-account" v={event.publication.toUpperCase()} />
              <Meta k="Sync status" v={b.sync_status || "not synced yet"} />
              {b.last_sync_at && <Meta k="Last sync" v={new Date(b.last_sync_at).toLocaleString()} />}
              {b.lovers_contact_id && <Meta k="Lovers contact ID" v={b.lovers_contact_id} />}
              {b.pro_contact_id && <Meta k="Pro contact ID" v={b.pro_contact_id} />}
              {b.last_error && <div className="pt-2" data-testid="ghl-last-error"><dt className="text-[#b35c5c] text-xs">Last error</dt><dd className="text-xs text-[#b35c5c] break-all">{b.last_error}</dd></div>}
            </dl>;
          })()}
          {(event.vendors_not_synced || []).length > 0 && <div className="mt-4 pt-4 border-t border-[#dcd3c6]" data-testid="vendors-not-synced">
            <p className="btw-overline mb-2">Not synced to GHL: no email</p>
            <ul className="text-sm text-[#5c5852] space-y-1">{event.vendors_not_synced.map((n, i) => <li key={i}>• {n}</li>)}</ul>
          </div>}
        </div>
      </details>

      <details className="bg-white border border-[#dcd3c6] p-5 mb-8" data-testid="status-history-section">
        <summary className="btw-overline cursor-pointer">Status history</summary>
        <div className="pt-4 space-y-1.5" data-testid="status-history">
          {(event.status_history || []).length === 0 && <p className="text-xs text-[#5c5852] italic">No status changes recorded.</p>}
          {(event.status_history || []).map((h, i) => <div key={i} className="flex items-center justify-between text-xs border-b border-[#dcd3c6]/50 pb-1.5">
            <span className="font-medium">{STATUS_LABELS[h.status] || h.status}</span>
            <span className="text-[#5c5852]">{new Date(h.at).toLocaleString()} · {h.by}</span>
          </div>)}
        </div>
      </details>

      {/* Danger zone — hard delete with name confirmation */}
      <section className="mt-8 bg-white border border-[#b35c5c] p-5" data-testid="danger-zone">
        <h2 className="btw-overline mb-2">Danger zone</h2>
        {event.status === "published" ? (
          <p className="text-sm text-[#5c5852]">Published features are never deleted here.</p>
        ) : !confirmDelete ? (
          <>
            <p className="text-sm text-[#5c5852] mb-3">
              Deletes this submission, its vendor credits and its Drive folder.
              GHL contacts are not touched. The removal is logged.
            </p>
            <button onClick={() => setConfirmDelete(true)} data-testid="delete-submission"
                    className="text-xs px-3 py-2 border border-[#b35c5c] text-[#b35c5c] hover:bg-[#b35c5c]/10">
              Delete this submission and its files
            </button>
          </>
        ) : (
          <div>
            <p className="text-sm text-black mb-3">
              Type <strong>{event.event_name}</strong> to confirm deletion.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                aria-label="Type the submission name to confirm deletion"
                data-testid="delete-confirm-name"
                className="btw-input border border-[#dcd3c6] px-3 py-2 bg-white text-sm w-64"
                value={deleteName}
                onChange={(e) => setDeleteName(e.target.value)}
              />
              <button onClick={hardDelete} disabled={deleting}
                      data-testid="delete-confirm-button"
                      className="text-xs px-3 py-2 bg-[#b35c5c] text-white border border-[#b35c5c] hover:opacity-85">
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
              <button onClick={() => { setConfirmDelete(false); setDeleteName(""); }}
                      className="text-xs text-[#5c5852] hover:underline">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const Meta = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-[#5c5852]">{k}</dt>
    <dd className="font-medium text-right">{v}</dd>
  </div>
);
